import type { ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@mlm/ui';
import { useHasPerm } from '../lib/auth-store';

/**
 * Phase 12.5 route guard. Renders the children when the current user has
 * the named permission; otherwise renders a "No access" card.
 *
 * super_admin always passes (handled inside `useHasPerm`). Permissions
 * are baked into the JWT at login — a recently-revoked perm doesn't
 * take effect until the user's next refresh.
 */
export function RequirePerm({
  perm,
  label,
  children,
}: {
  perm: string;
  /** Friendly label for the "no access" message. */
  label: string;
  children: ReactNode;
}) {
  const allowed = useHasPerm(perm);
  if (allowed) return <>{children}</>;
  return (
    <Card>
      <CardHeader>
        <CardTitle>No access</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          Your role doesn't include the{' '}
          <code className="font-mono text-xs px-1 py-0.5 rounded bg-muted">
            {perm}
          </code>{' '}
          permission, so the <strong>{label}</strong> page is hidden. Ask a
          super-admin to grant it via Members → Access.
        </p>
      </CardContent>
    </Card>
  );
}
